import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Typography,
  TableHead,
  Box,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  TableFooter,
  IconButton,
  TableContainer,
  Stack,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import PageContainer from 'src/components/container/PageContainer';
import ParentCard from 'src/components/shared/ParentCard';
import BlankCard from '../../components/shared/BlankCard';
import axios from 'axios';
import { links } from 'src/helpers/links';
import { IconDots, IconEdit, IconPlayCard, IconTrash } from '@tabler/icons';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import { useAuthChecker } from 'src/helpers/useAuthChecker';
import moment from 'moment';
import api from 'src/helpers/axios';
import CustomSwitch from 'src/components/forms/theme-elements/CustomSwitch';
import CustomFormLabel from 'src/components/forms/theme-elements/CustomFormLabel';
import Spinner from '../spinner/Spinner';
import DeleteModal from 'src/components/material-ui/modals/DeleteModal';

interface Project {
  _id: string;
  title: string;
  description: string;
  projectImage: string;
  isOnHomePage: number;
  isDeleted: number;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event: any) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: any) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: any) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: any) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const ProjectsTable = () => {
  useAuthChecker();
  const navigate = useNavigate();
  const notistack = useSnackbar();
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [changeFlag, setChangeFlag] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [menuOpen, setMenuOpen] = React.useState<null | React.Key>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [open, setOpen] = React.useState<React.Key | string>('');
  const handleOpenModal = (index: React.Key | string) => setOpen(index);
  const handleCloseModal = () => setOpen('');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, index: React.Key) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(index);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuOpen(null);
  };

  const getProjectsAPI = async () => {
    const { data } = await axios.get(links.baseURL + '/api/project');
    setProjects(data.result);
    setLoading(false);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - projects.length) : 0;

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteProject = async ({ _id }: { _id: string }) => {
    try {
      const response = await api.delete(links.baseURL + `/api/project/${_id}`);

      if (response) {
        notistack.enqueueSnackbar('Process completed!', {
          variant: 'success',
          anchorOrigin: {
            horizontal: 'right',
            vertical: 'bottom',
          },
        });
        console.log({ response });
      }
    } catch (error) {
      console.log({ error });
      notistack.enqueueSnackbar('Error deleting your brand', {
        variant: 'error',
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'bottom',
        },
      });
    } finally {
      setChangeFlag(!changeFlag);
      handleCloseModal();
    }
  };

  React.useEffect(() => {
    getProjectsAPI();
  }, [changeFlag]);

  const handleUpdateRow = ({ _id, updatedRow }: { _id: string; updatedRow: Project }) => {
    setLoading(true);
    api
      .put(`/api/project/${_id}`, updatedRow, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(() => {
        setChangeFlag(!changeFlag);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading)
    return (
      <ParentCard title="Projects">
        <>
          <CustomFormLabel htmlFor="project-title">Processing...</CustomFormLabel>
          <Spinner />
        </>
      </ParentCard>
    );
  else
    return (
      <PageContainer title="Projects" description="">
        <ParentCard title="Projects" withAdd additionRoute="/forms/form-project">
          <BlankCard>
            <TableContainer>
              <Table
                aria-label="custom pagination table"
                sx={{
                  whiteSpace: 'nowrap',
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="h6">Project Image / Title</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">Date of creation</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">Last updated</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">Visible on Home</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">Actions</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rowsPerPage > 0
                    ? projects.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    : projects
                  ).map((row: Project, index: React.Key) => {
                    return (
                      <TableRow key={index}>
                        <TableCell>
                          <Stack spacing={2} direction="row" alignItems="center">
                            <Avatar
                              src={links.uploads + row.projectImage}
                              alt={'Image'}
                              sx={{
                                borderRadius: '10px',
                                height: '70px',
                                width: '90px',
                              }}
                            />
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: {
                                    lg: 12,
                                    md: 11,
                                    sm: 10,
                                    xs: 10,
                                  },
                                }}
                                variant="h6"
                              >
                                {row.title}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Typography variant="subtitle2">
                            {moment(row.createdAt).format('LLLL')}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="subtitle2">
                            {moment(row.updatedAt).format('LLLL')}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          {/* <Typography variant="subtitle2">{row?.showInFooter}</Typography> */}
                          <CustomSwitch
                            checked={row?.isOnHomePage === 1 ? true : false}
                            onChange={() => {
                              const updatedIsOnHomePage = row?.isOnHomePage === 1 ? 0 : 1;
                              const updatedRow = { ...row, isOnHomePage: updatedIsOnHomePage };
                              handleUpdateRow({ _id: row?._id, updatedRow: updatedRow });
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <IconButton
                            id="basic-button"
                            aria-controls={menuOpen === index ? 'basic-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={menuOpen === index ? 'true' : undefined}
                            onClick={(e) => handleClick(e, index)}
                          >
                            <IconDots width={18} />
                          </IconButton>
                          <Menu
                            id="basic-menu"
                            anchorEl={anchorEl}
                            open={menuOpen === index}
                            onClose={handleClose}
                            MenuListProps={{
                              'aria-labelledby': 'basic-button',
                            }}
                          >
                            <MenuItem
                              onClick={() =>
                                navigate('/tables/projectGallery', {
                                  state: { _id: row._id },
                                })
                              }
                            >
                              <ListItemIcon>
                                <IconPlayCard width={18} />
                              </ListItemIcon>
                              Media
                            </MenuItem>
                            <MenuItem
                              onClick={() =>
                                navigate('/forms/form-project', {
                                  state: { _id: row._id },
                                })
                              }
                            >
                              <ListItemIcon>
                                <IconEdit width={18} />
                              </ListItemIcon>
                              Edit
                            </MenuItem>
                            <MenuItem onClick={() => handleOpenModal(index)}>
                              <ListItemIcon>
                                <IconTrash width={18} />
                              </ListItemIcon>
                              Delete
                            </MenuItem>
                          </Menu>
                        </TableCell>
                        <DeleteModal
                          open={open === index}
                          handleCloseModal={() => handleCloseModal()}
                          handleDelete={() => handleDeleteProject({ _id: row._id })}
                        />
                      </TableRow>
                    );
                  })}

                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                      colSpan={6}
                      count={projects?.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      SelectProps={{
                        native: true,
                      }}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </BlankCard>
        </ParentCard>
      </PageContainer>
    );
};

export default ProjectsTable;
